import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
    CreditCard, ArrowRight, ShieldCheck, CheckCircle2, Sparkles, 
    Globe, Laptop, AlertCircle, Mail, Key, Copy, Check, ChevronRight, Lock
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const Checkout = () => {
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();

    // Form inputs
    const [companyName, setCompanyName] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [projectName, setProjectName] = useState('');
    const [isDemo, setIsDemo] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState('Pro'); // Basic, Pro, Enterprise

    // Mock payment state
    const [cardNumber, setCardNumber] = useState('');
    const [cardExpiry, setCardExpiry] = useState('');
    const [cardCvc, setCardCvc] = useState('');
    const [cardName, setCardName] = useState('');

    // Onboarding result & statuses
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successData, setSuccessData] = useState(null);
    const [copied, setCopied] = useState(false);

    // Plan pricing details
    const plans = {
        Basic: { price: 49, desc: 'Ideal para constructoras medianas y obras individuales.', maxCameras: 2 },
        Pro: { price: 149, desc: 'Plan completo para constructoras con múltiples cámaras y visores 3D.', maxCameras: 5 },
        Enterprise: { price: 499, desc: 'Control multi-obra sin límites, soporte prioritario e integraciones custom.', maxCameras: 'Ilimitado' }
    };

    const handleOnboarding = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await axios.post('http://localhost:4000/api/auth/register-saas', {
                company_name: companyName,
                contact_email: contactEmail,
                project_name: projectName,
                plan_type: selectedPlan,
                is_demo: isDemo
            });

            setSuccessData(response.data);
        } catch (err) {
            console.error("Error en autoprovisionamiento SaaS:", err);
            if (err.response && err.response.data) {
                setError(err.response.data.error || 'Error al procesar el alta');
            } else {
                setError('No se pudo conectar con el servidor de aprovisionamiento.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (successData) {
        return (
            <div className="min-h-screen bg-theme-bg bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(37,99,235,0.15),rgba(255,255,255,0))] flex items-center justify-center p-4 md:p-8 font-sans">
                <div className="bg-theme-panel p-8 rounded-3xl border border-theme-border shadow-2xl w-full max-w-2xl text-center relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-emerald-500 to-teal-400"></div>
                    <div className="w-16 h-16 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 size={32} className="text-emerald-400" />
                    </div>

                    <h2 className="text-3xl font-black text-theme-text-primary tracking-tight mb-2">
                        {isDemo ? '¡Demo Provisionada con Éxito!' : '¡Suscripción Activada con Éxito!'}
                    </h2>
                    <p className="text-theme-text-secondary text-sm max-w-md mx-auto mb-8">
                        Hemos inicializado tu empresa, tu primera obra de construcción y asignado la licencia correspondiente.
                    </p>

                    <div className="bg-theme-bg/60 rounded-2xl border border-theme-border p-6 text-left space-y-4 mb-8">
                        <div className="flex justify-between items-center pb-3 border-b border-theme-border/60">
                            <div>
                                <span className="text-[10px] font-bold text-zinc-550 uppercase block">Empresa Cliente</span>
                                <span className="text-sm font-bold text-theme-text-primary">{companyName}</span>
                            </div>
                            <span className="bg-emerald-500/10 text-emerald-400 px-3 py-1 rounded-full text-[10px] font-bold border border-emerald-500/20">
                                {isDemo ? 'Demo Gratis (30 días)' : `Plan ${selectedPlan} Activo`}
                            </span>
                        </div>

                        <div>
                            <span className="text-[10px] font-bold text-zinc-550 uppercase block">Usuario Administrador</span>
                            <span className="text-xs font-mono text-zinc-300">{successData.adminUser?.email}</span>
                        </div>

                        <div className="relative">
                            <span className="text-[10px] font-bold text-zinc-550 uppercase block">Contraseña Temporal de Acceso</span>
                            <div className="flex items-center gap-2 mt-1.5 bg-theme-panel border border-theme-border px-3 py-2 rounded-xl">
                                <span className="text-xs font-mono font-bold text-blue-400 select-all">{successData.adminUser?.temporaryPassword}</span>
                                <button 
                                    onClick={() => handleCopy(successData.adminUser?.temporaryPassword)}
                                    className="ml-auto text-zinc-400 hover:text-theme-text-primary transition-colors"
                                    title="Copiar contraseña"
                                >
                                    {copied ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} />}
                                </button>
                            </div>
                        </div>

                        {successData.emailPreviewUrl && (
                            <div className="pt-2">
                                <a 
                                    href={successData.emailPreviewUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-xs text-amber-400 hover:underline flex items-center gap-1.5 font-bold"
                                >
                                    <Mail size={14} /> Ver Bandeja de Entrada (Simulada) para correo de introducción
                                </a>
                            </div>
                        )}
                    </div>

                    <button 
                        onClick={() => navigate('/login')}
                        className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3.5 rounded-xl transition-all shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2"
                    >
                        Ir al Portal e Iniciar Sesión <ChevronRight size={18} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-theme-bg bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(37,99,235,0.15),rgba(255,255,255,0))] text-theme-text-primary py-12 px-4 md:px-8 font-sans selection:bg-blue-500/30">
            <div className="max-w-6xl mx-auto">
                
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="flex justify-center items-center gap-3 mb-4">
                        <img src={isDarkMode ? "/logo-blanco-mark.png" : "/logo-color.png"} alt="Archi" className="h-12 w-auto" />
                        <div className="font-sans text-left leading-none tracking-tight">
                            <span className="text-2xl font-black uppercase tracking-wider">ARCHI</span>
                            <span className="text-2xl font-light text-blue-400 ml-1">24/7</span>
                        </div>
                    </div>
                    <h1 className="text-3xl md:text-5xl font-black tracking-tight text-theme-text-primary">
                        Comienza tu Control de Obra <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">SaaS en Minutos</span>
                    </h1>
                    <p className="text-theme-text-secondary mt-3 text-sm md:text-base max-w-2xl mx-auto">
                        Selecciona un plan comercial con checkout en línea o inicia tu demo gratis por 30 días de forma auto-provisionada.
                    </p>
                </div>

                {/* Main Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    
                    {/* Left: Configuration Form */}
                    <div className="lg:col-span-7 space-y-6">
                        
                        {/* Selector de Estatus */}
                        <div className="bg-theme-panel/40 backdrop-blur-xl border border-theme-border rounded-3xl p-4 flex gap-2">
                            <button
                                type="button"
                                onClick={() => { setIsDemo(false); }}
                                className={`flex-1 py-3 px-4 rounded-2xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                                    !isDemo ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'bg-transparent text-zinc-400 hover:text-zinc-300'
                                }`}
                            >
                                <Lock size={16} /> Suscripción Comercial
                            </button>
                            <button
                                type="button"
                                onClick={() => { setIsDemo(true); }}
                                className={`flex-1 py-3 px-4 rounded-2xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                                    isDemo ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-transparent text-zinc-400 hover:text-zinc-300'
                                }`}
                            >
                                <Sparkles size={16} /> Demo de Prueba (Gratis)
                            </button>
                        </div>

                        <form onSubmit={handleOnboarding} className="bg-theme-panel/40 backdrop-blur-xl border border-theme-border rounded-3xl p-6 md:p-8 space-y-6 shadow-xl">
                            <h3 className="text-lg font-bold text-theme-text-primary mb-4 flex items-center gap-2">
                                <Globe size={20} className="text-blue-400" /> 1. Datos Generales de Registro
                            </h3>

                            {error && (
                                <div className="bg-red-500/10 border border-red-500/40 text-red-500 text-xs font-bold p-4 rounded-xl flex items-center gap-2">
                                    <AlertCircle size={16} /> {error}
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-bold text-zinc-550 uppercase tracking-wider mb-2">Nombre de la Empresa</label>
                                    <input 
                                        type="text"
                                        required
                                        value={companyName}
                                        onChange={(e) => setCompanyName(e.target.value)}
                                        className="w-full bg-theme-bg border border-theme-border text-theme-text-primary px-4 py-3 rounded-xl focus:outline-none focus:border-blue-500 text-xs transition-colors"
                                        placeholder="Ej: Constructora Delta"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-zinc-550 uppercase tracking-wider mb-2">Correo del Admin de Empresa</label>
                                    <input 
                                        type="email"
                                        required
                                        value={contactEmail}
                                        onChange={(e) => setContactEmail(e.target.value)}
                                        className="w-full bg-theme-bg border border-theme-border text-theme-text-primary px-4 py-3 rounded-xl focus:outline-none focus:border-blue-500 text-xs transition-colors"
                                        placeholder="admin@constructoradelta.com"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold text-zinc-550 uppercase tracking-wider mb-2">Nombre de la Primera Obra / Proyecto</label>
                                <input 
                                    type="text"
                                    required
                                    value={projectName}
                                    onChange={(e) => setProjectName(e.target.value)}
                                    className="w-full bg-theme-bg border border-theme-border text-theme-text-primary px-4 py-3 rounded-xl focus:outline-none focus:border-blue-500 text-xs transition-colors"
                                    placeholder="Ej: Torre Corporativa Poniente"
                                />
                            </div>

                            {!isDemo && (
                                <div className="pt-4 border-t border-theme-border/60 space-y-6">
                                    <h3 className="text-lg font-bold text-theme-text-primary mb-4 flex items-center gap-2">
                                        <CreditCard size={20} className="text-blue-400" /> 2. Método de Pago Seguro (Simulado)
                                    </h3>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-2">
                                            <label className="block text-[10px] font-bold text-zinc-550 uppercase tracking-wider mb-2">Titular de la Tarjeta</label>
                                            <input 
                                                type="text"
                                                required={!isDemo}
                                                value={cardName}
                                                onChange={(e) => setCardName(e.target.value)}
                                                className="w-full bg-theme-bg border border-theme-border text-theme-text-primary px-4 py-3 rounded-xl focus:outline-none focus:border-blue-500 text-xs transition-colors"
                                                placeholder="Nombre tal como aparece"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-zinc-550 uppercase tracking-wider mb-2">Número de Tarjeta</label>
                                            <input 
                                                type="text"
                                                required={!isDemo}
                                                maxLength={19}
                                                value={cardNumber}
                                                onChange={(e) => setCardNumber(e.target.value.replace(/\s?/g, '').replace(/(\d{4})/g, '$1 ').trim())}
                                                className="w-full bg-theme-bg border border-theme-border text-theme-text-primary px-4 py-3 rounded-xl focus:outline-none focus:border-blue-500 text-xs transition-colors font-mono"
                                                placeholder="4000 1234 5678 9010"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-[10px] font-bold text-zinc-550 uppercase tracking-wider mb-2">Vence</label>
                                                <input 
                                                    type="text"
                                                    required={!isDemo}
                                                    maxLength={5}
                                                    value={cardExpiry}
                                                    onChange={(e) => setCardExpiry(e.target.value)}
                                                    className="w-full bg-theme-bg border border-theme-border text-theme-text-primary px-4 py-3 rounded-xl focus:outline-none focus:border-blue-500 text-xs transition-colors font-mono"
                                                    placeholder="MM/AA"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-zinc-550 uppercase tracking-wider mb-2">CVC</label>
                                                <input 
                                                    type="password"
                                                    required={!isDemo}
                                                    maxLength={3}
                                                    value={cardCvc}
                                                    onChange={(e) => setCardCvc(e.target.value)}
                                                    className="w-full bg-theme-bg border border-theme-border text-theme-text-primary px-4 py-3 rounded-xl focus:outline-none focus:border-blue-500 text-xs transition-colors font-mono"
                                                    placeholder="•••"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 text-[10px] text-zinc-500 bg-theme-bg/60 p-3.5 rounded-2xl border border-theme-border">
                                        <ShieldCheck size={16} className="text-emerald-400 shrink-0" />
                                        <span>Procesamiento de pagos simulado con fines demostrativos. Tus datos de tarjeta no son almacenados.</span>
                                    </div>
                                </div>
                            )}

                            <button 
                                type="submit"
                                disabled={loading}
                                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3.5 mt-4 rounded-xl transition-all shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
                                ) : isDemo ? (
                                    <>Aprovisionar Demo Gratis <Sparkles size={16} /></>
                                ) : (
                                    <>Confirmar Pago e Iniciar Obra <ArrowRight size={16} /></>
                                )}
                            </button>
                        </form>
                    </div>

                    {/* Right: Plan details Card */}
                    <div className="lg:col-span-5 space-y-6">
                        
                        {/* Selector de Plan */}
                        <div className="bg-theme-panel/40 backdrop-blur-xl border border-theme-border rounded-3xl p-6 shadow-xl">
                            <h3 className="text-sm font-bold text-theme-text-primary mb-4">Selecciona tu Plan</h3>
                            <div className="space-y-3">
                                {Object.keys(plans).map((planKey) => {
                                    const plan = plans[planKey];
                                    const isSelected = selectedPlan === planKey;
                                    return (
                                        <button
                                            key={planKey}
                                            type="button"
                                            onClick={() => setSelectedPlan(planKey)}
                                            className={`w-full text-left p-4 rounded-2xl border transition-all relative overflow-hidden flex justify-between items-center ${
                                                isSelected 
                                                    ? 'bg-blue-600/10 border-blue-500 shadow-md shadow-blue-900/10' 
                                                    : 'bg-theme-bg/40 border-theme-border hover:border-theme-border/80'
                                            }`}
                                        >
                                            <div>
                                                <span className="text-xs font-black text-theme-text-primary block">{planKey}</span>
                                                <span className="text-[10px] text-zinc-400 mt-1 block max-w-[200px] leading-relaxed">{plan.desc}</span>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-lg font-black text-theme-text-primary block">${plan.price} USD</span>
                                                <span className="text-[9px] font-bold text-zinc-550 block">/ mes</span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Order Summary */}
                        <div className="bg-theme-panel/40 backdrop-blur-xl border border-theme-border rounded-3xl p-6 shadow-xl space-y-4">
                            <h3 className="text-sm font-bold text-theme-text-primary border-b border-theme-border pb-3">Resumen de Alta</h3>
                            
                            <div className="space-y-2.5 text-xs">
                                <div className="flex justify-between text-zinc-400">
                                    <span>Plan Elegido:</span>
                                    <span className="font-bold text-theme-text-primary">{selectedPlan}</span>
                                </div>
                                <div className="flex justify-between text-zinc-400">
                                    <span>Límite de Cámaras:</span>
                                    <span className="font-bold text-theme-text-primary">{plans[selectedPlan].maxCameras}</span>
                                </div>
                                <div className="flex justify-between text-zinc-400">
                                    <span>Frecuencia:</span>
                                    <span className="font-bold text-theme-text-primary">Mensual</span>
                                </div>
                                <div className="h-px bg-theme-border my-2"></div>
                                <div className="flex justify-between text-sm font-black">
                                    <span>Total a Pagar:</span>
                                    <span className="text-emerald-400">${isDemo ? 0 : plans[selectedPlan].price} USD</span>
                                </div>
                            </div>
                        </div>

                        {/* Mock Card Preview */}
                        {!isDemo && (cardName || cardNumber) && (
                            <div className="bg-gradient-to-br from-zinc-800 to-zinc-950 border border-zinc-700/60 rounded-3xl p-6 shadow-2xl relative overflow-hidden aspect-[1.586/1] text-zinc-300">
                                <div className="absolute right-6 top-6 w-12 h-8 bg-zinc-700/30 rounded-lg flex items-center justify-center font-bold text-[10px] border border-zinc-700/50">VISA</div>
                                <div className="mt-8 font-mono tracking-widest text-lg md:text-xl font-bold">{cardNumber || '•••• •••• •••• ••••'}</div>
                                <div className="mt-10 flex justify-between items-end">
                                    <div>
                                        <span className="text-[8px] uppercase tracking-wider block text-zinc-550">Titular</span>
                                        <span className="font-bold uppercase text-xs tracking-wide">{cardName || 'Titular de la Tarjeta'}</span>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-[8px] uppercase tracking-wider block text-zinc-550">Vence</span>
                                        <span className="font-bold text-xs">{cardExpiry || 'MM/AA'}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>

                </div>

            </div>
        </div>
    );
};

export default Checkout;
